import React, { useState, useMemo } from 'react';
import { X, ChevronsRight, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { saveBulkOverridesV3 } from '../../api';
import { formatCurrency } from '../../utils/formatters';
// eslint-disable-next-line import/no-relative-packages
import { currencySymbol } from '../../../../utils/currency';
import type { ForecastAccount } from '../../types';
import {
  SpreadBasis,
  readSavedSpreadBasis,
  saveSpreadBasis,
  clearSpreadBasis,
  resolveSpreadBasis,
  spreadAllocations,
  spreadBasisHasData,
  spreadBasisLabel,
  spreadBasisPreference,
  spreadBasisSourceName,
} from '../../utils/spreadBasis';

interface DepartmentSpreadModalProps {
  isOpen: boolean;
  onClose: () => void;
  departmentName: string;
  month: number;          // 1-12
  monthKey: string;       // 'jan', 'feb', etc.
  childAccounts: ForecastAccount[];
  isRevenue: boolean;
  /** Budget versions default to the prior-year published budget as the spread basis. */
  isBudget?: boolean;
  versionId: number;
  orgId: number;
  locationId: number;
  year: number;
  initialAmount?: number; // Pre-fill amount when opened from inline cell entry
  currency?: string | null;
  onSuccess: () => void;
  onBulkSave?: (entries: { chartOfAccountId: number; month: number; value: number }[]) => Promise<void>;
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

export function DepartmentSpreadModal({
  isOpen,
  onClose,
  departmentName,
  month,
  monthKey,
  childAccounts,
  isRevenue,
  isBudget = false,
  versionId,
  orgId,
  locationId,
  year,
  initialAmount,
  currency,
  onSuccess,
  onBulkSave,
}: DepartmentSpreadModalProps) {
  const [amount, setAmount] = useState(() =>
    initialAmount != null ? String(initialAmount) : ''
  );
  // Init spread basis: saved preference for this version, else the mode
  // default (budget versions → PY published budget). If that basis has no
  // data for this month, fall back to the first basis that does.
  const [{ spreadBasis, autoSwitchedFrom }, setBasisState] = useState<{
    spreadBasis: SpreadBasis;
    autoSwitchedFrom: SpreadBasis | null;
  }>(() => {
    const requested = readSavedSpreadBasis(versionId) ?? spreadBasisPreference(isBudget)[0];
    const resolved = resolveSpreadBasis(childAccounts, month, requested, isBudget);
    return {
      spreadBasis: resolved?.basis ?? requested,
      autoSwitchedFrom: resolved?.fellBack ? requested : null,
    };
  });
  const setSpreadBasis = (basis: SpreadBasis) => setBasisState({ spreadBasis: basis, autoSwitchedFrom: null });
  // Default to true — on first open (no saved basis) we want "remember" pre-checked
  const [rememberBasis, setRememberBasis] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const parsedAmount = parseFloat(amount.replace(/,/g, ''));
  const isValidAmount = !isNaN(parsedAmount) && parsedAmount !== 0;

  // Count accounts with existing overrides on this month
  const existingOverrideCount = useMemo(() => {
    return childAccounts.filter(a => a.monthlyOverrides?.[monthKey] === true).length;
  }, [childAccounts, monthKey]);

  const handleSetSpreadBasis = (basis: SpreadBasis) => {
    setSpreadBasis(basis);
    if (rememberBasis) saveSpreadBasis(versionId, basis);
  };

  const handleSetRemember = (checked: boolean) => {
    setRememberBasis(checked);
    if (checked) {
      saveSpreadBasis(versionId, spreadBasis);
    } else {
      clearSpreadBasis(versionId);
    }
  };

  // Calculate spread allocations based on selected basis (shared with the
  // inline grid path so both entry points spread identically)
  const previewRows = useMemo(
    () => spreadAllocations(childAccounts, spreadBasis, month, isValidAmount ? parsedAmount : 0),
    [childAccounts, month, spreadBasis, parsedAmount, isValidAmount]
  );

  const basisOptions = useMemo(
    () => spreadBasisPreference(isBudget).map(value => ({
      value,
      ...spreadBasisLabel(value, year, month),
      hasData: spreadBasisHasData(childAccounts, value, month),
    })),
    [childAccounts, isBudget, year, month]
  );

  const totalAllocated = previewRows.reduce((sum, r) => sum + r.allocated, 0);
  const noBasisData = previewRows.every(r => r.basisValue === 0);
  const appliedCount = previewRows.filter(r => r.allocated !== 0).length;

  const handleApplyClick = () => {
    if (!isValidAmount || childAccounts.length === 0) return;
    // If any child accounts already have overrides, ask for confirmation first
    if (existingOverrideCount > 0) {
      setShowConfirm(true);
    } else {
      doApply();
    }
  };

  const doApply = async () => {
    setShowConfirm(false);
    setIsSaving(true);
    // Persist basis preference if "remember" is checked
    if (rememberBasis) saveSpreadBasis(versionId, spreadBasis);
    try {
      // Save overrides for ALL child accounts — zero-allocation accounts get 0
      // so existing overrides are always cleared and the total matches exactly
      if (onBulkSave) {
        // Optimistic path: values appear immediately in the grid
        await onBulkSave(previewRows.map(r => ({
          chartOfAccountId: r.account.chartOfAccountId,
          month,
          value: r.allocated,
        })));
      } else {
        await saveBulkOverridesV3(versionId, previewRows.map(r => ({
          chartOfAccountId: r.account.chartOfAccountId,
          year,
          month,
          overrideAmount: r.allocated,
        })));
      }

      toast.success(`Applied overrides to ${previewRows.length} account${previewRows.length !== 1 ? 's' : ''}`);
      onSuccess();
    } catch (err) {
      console.error('Error applying department spread:', err);
      toast.error('Failed to apply overrides. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setAmount('');
    setShowConfirm(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fv3-scrim fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <div className="fv3-dialog bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="fv3-dialog-head flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="fv3-dialog-icon p-2 rounded-lg" style={{ background: 'var(--lodge-orange-100)' }}>
              <ChevronsRight className="w-5 h-5" style={{ color: 'var(--lodge-orange-500)' }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Spread Department Amount</h2>
              <p className="text-sm text-slate-500">
                {departmentName} — {MONTH_NAMES[month - 1]} {year}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Confirmation overlay */}
        {showConfirm && (
          <div className="absolute inset-0 bg-white/95 rounded-xl flex flex-col items-center justify-center p-8 z-10">
            <div className="p-3 bg-amber-100 rounded-full mb-4">
              <AlertCircle className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2 text-center">Overwrite Existing Overrides?</h3>
            <p className="text-sm text-slate-600 text-center mb-6 max-w-sm">
              {existingOverrideCount} account{existingOverrideCount !== 1 ? 's' : ''} already {existingOverrideCount !== 1 ? 'have' : 'has'} overrides for {MONTH_NAMES[month - 1]}.
              All sub-account overrides will be replaced — accounts not included in the spread will be set to $0.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-5 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={doApply}
                className="px-5 py-2 text-sm font-medium rounded-lg transition-all"
                style={{ background: 'var(--lodge-orange-400)', color: '#ffffff', border: 'none', boxShadow: 'var(--shadow-orange)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; }}
              >
                Yes, Overwrite
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Amount input */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Department Total Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">{currencySymbol(currency)}</span>
              <input
                type="text"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApplyClick()}
                placeholder="0"
                autoFocus
                className="w-full pl-7 pr-4 py-2.5 border border-slate-300 rounded-lg text-slate-900 text-lg font-medium focus:outline-none"
                onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 2px var(--lodge-orange-400)'; e.currentTarget.style.borderColor = 'transparent'; }}
                onBlur={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = ''; }}
              />
            </div>
          </div>

          {/* Spread basis */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Spread Basis
            </label>
            <div className="flex flex-col gap-2">
              {basisOptions.map(option => (
                <label
                  key={option.value}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    spreadBasis === option.value
                      ? ''
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                  style={spreadBasis === option.value ? {
                    borderColor: 'var(--lodge-orange-400)',
                    background: 'var(--lodge-orange-50)',
                  } : undefined}
                >
                  <input
                    type="radio"
                    name="spreadBasis"
                    value={option.value}
                    checked={spreadBasis === option.value}
                    onChange={() => handleSetSpreadBasis(option.value)}
                    className="mt-0.5"
                    style={{ accentColor: 'var(--lodge-orange-400)' }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800">{option.label}</span>
                      {!option.hasData && (
                        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400 bg-slate-100 rounded px-1.5 py-0.5">
                          No data
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
            {autoSwitchedFrom && (
              <p className="mt-2 text-xs text-slate-500 flex items-center gap-1.5">
                <Info size={12} className="text-slate-400 flex-shrink-0" />
                No {spreadBasisSourceName(autoSwitchedFrom, year, month)} data for this month — using {spreadBasisLabel(spreadBasis, year, month).label.replace(/^% of /, '')} instead.
              </p>
            )}

            {/* Remember setting */}
            <div className="flex items-center gap-2 mt-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberBasis}
                  onChange={(e) => handleSetRemember(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-slate-300"
                  style={{ accentColor: 'var(--lodge-orange-400)' }}
                />
                <span className="text-xs text-slate-500">
                  Remember this setting for this version
                </span>
              </label>
              <span
                title="When enabled, your preferred spread basis is saved for this version — so every department entry opens pre-configured and ready to apply, streamlining overrides across months and departments without repeated setup."
                className="flex-shrink-0 cursor-help"
              >
                <Info size={13} className="text-slate-400" />
              </span>
            </div>
          </div>

          {/* No basis data warning */}
          {noBasisData && isValidAmount && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                No {spreadBasisSourceName(spreadBasis, year, month)} data available. Allocations will be {formatCurrency(0, true, currency)}.
              </p>
            </div>
          )}

          {/* Preview table */}
          {childAccounts.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-slate-700">Account Allocations Preview</h3>
                {isValidAmount && !noBasisData && (
                  <span className="text-xs text-slate-500">
                    Total: {formatCurrency(totalAllocated, true, currency)}
                  </span>
                )}
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-slate-100">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Account</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Basis</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">%</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Allocated</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {previewRows.map(row => (
                      <tr key={row.account.chartOfAccountId} className="hover:bg-slate-50">
                        <td className="px-3 py-2">
                          <div className="text-xs font-mono text-slate-500">{row.account.accountNumber}</div>
                          <div className="text-sm text-slate-700">{row.account.accountName}</div>
                        </td>
                        <td className="px-3 py-2 text-right text-sm text-slate-600 tabular-nums">
                          {formatCurrency(row.basisValue, true, currency)}
                        </td>
                        <td className="px-3 py-2 text-right text-sm text-slate-500 tabular-nums">
                          {row.pct.toFixed(1)}%
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <span className={`text-sm font-medium ${row.allocated !== 0 ? 'text-slate-900' : 'text-slate-400'}`}>
                            {isValidAmount ? formatCurrency(row.allocated, true, currency) : '—'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="fv3-dialog-foot flex items-center justify-between px-6 py-4 border-t border-slate-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleApplyClick}
            disabled={!isValidAmount || isSaving || noBasisData}
            className="flex items-center gap-2 px-5 py-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium rounded-lg transition-all"
            style={{ background: 'var(--lodge-orange-400)', color: '#ffffff', border: 'none', boxShadow: 'var(--shadow-orange)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; }}
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Applying...
              </>
            ) : (
              <>
                <CheckCircle2 size={16} />
                Apply {appliedCount} Override{appliedCount !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
